import React, { useState, useEffect, useRef } from 'react';
import Loader from '../molecules/Loader';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';

interface CommonIFrameProps {
    src: string;
    title: string;
    id?: string;
    buffer?: number;
    delay?: number;
    className?: string;
    loaderClassName?: string;
}

const CommonIFrame = ({
    src,
    title,
    id = 'common-iframe',
    buffer = 60,
    delay = 1000,
    className = '',
    loaderClassName = '',
}: CommonIFrameProps) => {
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);
    const [showIframe, setShowIframe] = useState(false);

    const tableHeight = useRef<number | null>(0);

    // This hook will give height to fill the screen height
    tableHeight.current = useFillHeightToFitScreen({
        id,
        height: tableHeight.current,
        buffer, //footer
        triggerDummyRender: true,
    });

    useEffect(() => {
        // Show iframe after specified delay, but keep loader until iframe loads
        const timer = setTimeout(() => {
            setShowIframe(true);
        }, delay);

        return () => clearTimeout(timer);
    }, [delay]);

    const handleIframeLoad = () => {
        setIsIframeLoaded(true);
    };

    const handleIframeError = () => {
        console.error(`Failed to load iframe: ${title}`);
        setIsIframeLoaded(true); // Hide loader even on error
    };

    return (
        <div
            id={id}
            className={`w-full border-0 bg-white relative ${className}`}
            style={{
                height: tableHeight.current || 'calc(100vh - var(--navbar-height))',
                margin: 0,
                padding: 0,
            }}
        >
            {/* Show loader until iframe is loaded */}
            {!isIframeLoaded && (
                <div className={`absolute inset-0 z-10 bg-white ${loaderClassName}`}>
                    <Loader style={{ height: '100%' }} />
                </div>
            )}

            {/* Show iframe after delay */}
            {showIframe && (
                <iframe
                    className="w-full border-0"
                    style={{
                        height: tableHeight.current || 'calc(100vh - var(--navbar-height))',
                        margin: 0,
                        padding: 0,
                    }}
                    src={src}
                    title={title}
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                />
            )}
        </div>
    );
};

export default CommonIFrame;
